<template>
  <div class="info-banner" :class="bannerClass">
    <div class="banner-content">
      <!-- Label -->
      <span v-if="label" class="banner-label">{{ label }}</span>

      <!-- Value -->
      <span class="banner-value" :class="{ 'banner-value--mono': mono }">
        <slot>{{ value }}</slot>
      </span>
    </div>

    <!-- Action Button (Copy, etc.) -->
    <button
      v-if="copyable"
      type="button"
      class="banner-action"
      @click="handleCopy"
    >
      <svg v-if="!copied" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      {{ copied ? 'Copied!' : copyText }}
    </button>

    <!-- Custom Action Slot -->
    <slot name="action"></slot>
  </div>
</template>

<script>
import { responses } from '@/global-variables/responses'

export default {
  name: 'InfoBanner',

  props: {
    label: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'success', 'warning', 'dark'].includes(value)
    },
    copyable: {
      type: Boolean,
      default: false
    },
    copyText: {
      type: String,
      default: 'Copy'
    },
    copyValue: {
      type: String,
      default: ''
    },
    mono: {
      type: Boolean,
      default: true
    },
    successMessage: {
      type: String,
      default: 'Copied to clipboard'
    }
  },

  data() {
    return {
      copied: false,
      copyTimer: null
    }
  },

  beforeDestroy() {
    if (this.copyTimer) {
      clearTimeout(this.copyTimer)
    }
  },

  computed: {
    bannerClass() {
      return `banner--${this.variant}`
    },
    valueToCopy() {
      return this.copyValue || this.value || this.$slots.default?.[0]?.text || ''
    }
  },

  methods: {
    async handleCopy() {
      const value = this.valueToCopy

      if (!value) return

      try {
        if (window.copy) {
          window.copy(value)
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = value
          textArea.style.position = 'fixed'
          textArea.style.left = '-9999px'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }

        this.copied = true
        this.$emit('copy', value)

        if (this.successMessage) {
          responses.showSuccess(this.successMessage)
        }

        // Clear existing timer if any
        if (this.copyTimer) {
          clearTimeout(this.copyTimer)
        }

        this.copyTimer = setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        responses.throwErr('Failed to copy to clipboard', 'Copy Error')
      }
    }
  }
}
</script>

<style scoped>
.info-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4, 1rem);
  padding: var(--space-4, 1rem) var(--space-5, 1.25rem);
  border-radius: var(--radius-lg, 0.5rem);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Variants */
.banner--primary {
  background: linear-gradient(135deg, var(--color-primary-500, #0ea5e9) 0%, var(--color-primary-600, #0284c7) 100%);
}

.banner--success {
  background: linear-gradient(135deg, var(--color-success-500, #10b981) 0%, var(--color-success-600, #059669) 100%);
}

.banner--warning {
  background: linear-gradient(135deg, var(--color-warning-500, #f59e0b) 0%, var(--color-warning-600, #d97706) 100%);
}

.banner--dark {
  background: linear-gradient(135deg, var(--color-gray-800, #27272a) 0%, var(--color-gray-900, #18181b) 100%);
}

/* Content */
.banner-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1, 0.25rem);
  min-width: 0;
}

/* Label */
.banner-label {
  font-size: var(--text-xs, 0.6875rem);
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: var(--font-medium, 500);
}

/* Value */
.banner-value {
  font-size: var(--text-xl, 1rem);
  font-weight: var(--font-bold, 700);
  color: #ffffff;
  word-break: break-all;
}

.banner-value--mono {
  font-family: var(--font-family-mono, monospace);
  font-size: var(--text-lg, 0.9375rem);
}

/* Action Button */
.banner-action {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1, 0.25rem);
  padding: var(--space-2, 0.5rem) var(--space-3, 0.75rem);
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: var(--radius-md, 0.375rem);
  color: #ffffff;
  font-size: var(--text-sm, 0.75rem);
  font-weight: var(--font-medium, 500);
  cursor: pointer;
  transition: all var(--transition-base, 0.2s);
  white-space: nowrap;
  flex-shrink: 0;
}

.banner-action:hover {
  background: rgba(255, 255, 255, 0.3);
}

.banner-action svg {
  width: 14px;
  height: 14px;
}

/* Responsive */
@media (max-width: 576px) {
  .info-banner {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3, 0.75rem);
    padding: var(--space-4, 1rem);
  }

  .banner-action {
    width: 100%;
    justify-content: center;
  }

  .banner-value {
    font-size: var(--text-lg, 0.9375rem);
  }

  .banner-value--mono {
    font-size: var(--text-md, 0.875rem);
  }
}
</style>
